import Select, { MenuPosition, Options } from 'react-select';
import { ComboBoxProps } from './ComboBoxCommon';
import { classNames } from '../../../style/common';
import { ComboBoxOption } from '../../../models/Input';

export default function ModernComboBox<
  IsMulti extends boolean,
  ComboBoxOptionValueType
>(
  props: ComboBoxProps<IsMulti, ComboBoxOptionValueType> & {
    placeholder?: string,
    isClearable?: boolean,
    isDisabled?: boolean,
    isSearchable?: boolean,
    closeMenuOnSelect?: boolean,
    menuPosition?: MenuPosition | undefined,
    isOptionDisabled?: ((option: ComboBoxOption<ComboBoxOptionValueType>, selectValue: Options<ComboBoxOption<ComboBoxOptionValueType>>) => boolean) | undefined
  }
) {

  const hasValidationErrors = () => {
    if (!props.required) {
      return false;
    }

    if (!props.selections) {
      return true;
    }

    if (props.selections instanceof Array) {
      return !!!props.selections.length;
    }

    return false;
  }

  return (
    <Select
      isMulti={props.isMulti}
      closeMenuOnSelect={props.closeMenuOnSelect}
      value={props.selections}
      defaultValue={props.defaultOption}
      onChange={props.onChange}
      onKeyDown={props.onKeyDown}
      inputValue={props.inputValue}
      onInputChange={props.onInputChange}
      menuPlacement='auto'
      menuPosition={props.menuPosition}
      isSearchable={props.isSearchable}
      isClearable={props.isClearable}
      options={props.options}
      required={props.required}
      placeholder={props.placeholder}
      menuPortalTarget={document.body}
      isOptionDisabled={props.isOptionDisabled}
      classNames={{
        container: (internalProps) => classNames(
          "!border-b-2",
          internalProps.isFocused
            ? hasValidationErrors() ? "!border-b-error-contrast" : "!border-b-emphasis"
            : hasValidationErrors() ? "!border-b-error-secondary" : "!border-b-tertiary"
        ),
        singleValue: (internalProps) => classNames(
          "!text-contrast-primary",
          props.classNames?.singleValue ? props.classNames.singleValue(internalProps) : undefined
        ),
        multiValue: (internalProps) => classNames(
          props.classNames?.multiValue ? props.classNames.multiValue(internalProps) : undefined
        ),
        multiValueLabel: () => classNames(
          "!text-contrast-primary"
        ),
        indicatorsContainer: (internalProps) => classNames(props.classNames?.indicatorsContainer ? props.classNames.indicatorsContainer(internalProps) : undefined),
        clearIndicator: (internalProps) => classNames(internalProps.isFocused ? "text-emphasis" : "text-contrast-primary"),
        input: (internalProps) => classNames(
          "!text-contrast-primary"
        ),
        placeholder: () => "!text-contrast-tertiary",
        menu: () => classNames(
          "!bg-secondary !border !border-color-border"
        ),
        option: (internalProps) => classNames(
          "!bg-secondary",
          internalProps.isDisabled ? "!text-contrast-quarternary" : "hover:!bg-tertiary !text-contrast-primary"
        ),
        valueContainer: (internalProps) => classNames(props.classNames?.valueContainer ? props.classNames.valueContainer(internalProps) : undefined)
      }}
      styles={{
        container: (base, internalProps) => ({
          ...base,
          minHeight: 0,
          ...(props.styles?.container ? props.styles.container(base, internalProps) : {})
        }),
        valueContainer: (base, internalProps) => ({ ...base, padding: 0, flex: "unset", ...(props.styles?.valueContainer ? props.styles.valueContainer(base, internalProps) : {}) }),
        singleValue: base => ({ ...base, margin: 0 }),
        menuPortal: (base, internalProps) => ({ ...base, zIndex: 9999, boxShadow: 'none', ...(props.styles?.menuPortal ? props.styles.menuPortal(base, internalProps) : {}) }),
        control: (base, internalProps) => ({
          ...base,
          height: "100%",
          background: "transparent",
          border: 'none',
          borderRadius: 'none',
          boxShadow: 'none',
          minHeight: 0,
          padding: "0 4px",
          flexWrap: "unset",
          ':hover': {

          },
          ...(props.styles?.control ? props.styles.control(base, internalProps) : {})
        }),
        dropdownIndicator: base => ({ ...base, padding: 0, margin: 0 }),
        indicatorsContainer: base => ({ ...base, marginLeft: 0, marginRight: 0 }),
        clearIndicator: (base, internalProps) => ({
          ...base,
          padding: 0,
          marginLeft: "20px",
          cursor: "pointer"
        }),
        indicatorSeparator: base => ({ ...base, display: "none" }),
        multiValue: base => ({ ...base, background: "none" }),
        multiValueLabel: base => ({ ...base, margin: 0, padding: 0, lineHeight: ".75rem" }),
        multiValueRemove: (base, innerProps) => ({ ...base }),
        placeholder: base => ({ ...base }),
        input: (base) => ({
          ...base,
          padding: 0,
          margin: 0,
          minWidth: "30px",
          'input:focus': {
            boxShadow: 'none',
          },
        })
      }} />
  );
}